// Typed access to the portal API.
//
// Functions over a client rather than a class, so a caller can hold one
// `PortalApi` built from that app's ports and nothing reaches for a global.

import type { AxiosInstance } from 'axios';
import type {
  ApiEnvelope,
  CommunicationLog,
  DeleteAccountResult,
  LinkedTenant,
  Pagination,
  PortalConfig,
  PortalInvoice,
  PortalPayment,
  PortalSummary,
  VerifyResult,
} from '../types';

export interface ListInvoicesParams {
  page?: number;
  limit?: number;
  status?: string;
  search?: string;
}

export interface ListInvoicesResult {
  invoices: PortalInvoice[];
  pagination: Pagination;
}

export interface PortalApi {
  invoices: {
    list(params?: ListInvoicesParams): Promise<ListInvoicesResult>;
    get(id: string): Promise<PortalInvoice>;
    logs(id: string): Promise<CommunicationLog[]>;
    paymentLink(id: string): Promise<{ paymentUrl: string; orderId: string }>;
    /** Raw PDF bytes. Each platform decides what to do with them. */
    downloadPdf(id: string): Promise<ArrayBuffer>;
  };
  payments: { list(): Promise<PortalPayment[]> };
  summary: { get(): Promise<PortalSummary> };
  tenants: {
    listMine(): Promise<LinkedTenant[]>;
    confirmLink(tenantId: string): Promise<void>;
  };
  account: { remove(): Promise<DeleteAccountResult> };
  system: { portalConfig(tenantId: string): Promise<PortalConfig> };
  auth: {
    requestAccess(identifier: string, tenantId: string, codeChallenge?: string): Promise<void>;
    verify(token: string, codeVerifier?: string): Promise<VerifyResult>;
  };
}

export function createPortalApi(http: AxiosInstance): PortalApi {
  const unwrap = <T>(response: { data: ApiEnvelope<T> }): T => response.data.data;

  return {
    invoices: {
      list: async (params = {}) =>
        unwrap<ListInvoicesResult>(
          await http.get('/portal/invoices', {
            params: {
              page: params.page,
              limit: params.limit,
              status: params.status || undefined,
              search: params.search?.trim() || undefined,
            },
          })
        ),
      get: async id => unwrap<PortalInvoice>(await http.get(`/portal/invoices/${id}`)),
      logs: async id => unwrap<CommunicationLog[]>(await http.get(`/portal/invoices/${id}/logs`)),
      paymentLink: async id =>
        unwrap<{ paymentUrl: string; orderId: string }>(
          await http.get(`/portal/invoices/${id}/payment-link`)
        ),
      downloadPdf: async id => {
        // Behind an Authorization header, so the URL cannot simply be opened —
        // every platform has to fetch it and then do something native with the
        // bytes. arraybuffer rather than blob because Blob is a DOM type: the
        // web adapter wraps these bytes in one, native writes them to a file.
        const response = await http.get(`/portal/invoices/${id}/download`, {
          responseType: 'arraybuffer',
        });
        return response.data as ArrayBuffer;
      },
    },

    payments: {
      list: async () =>
        unwrap<{ payments: PortalPayment[] }>(await http.get('/portal/payments')).payments,
    },

    summary: { get: async () => unwrap<PortalSummary>(await http.get('/portal/summary')) },

    tenants: {
      listMine: async () =>
        unwrap<{ tenants: LinkedTenant[] }>(await http.get('/portal/me/tenants')).tenants,
      confirmLink: async tenantId => {
        await http.post(`/portal/me/tenants/${tenantId}/confirm`);
      },
    },

    account: {
      remove: async () => unwrap<DeleteAccountResult>(await http.delete('/portal/me')),
    },

    system: {
      portalConfig: async tenantId =>
        unwrap<PortalConfig>(await http.get('/system/portal-config', { params: { tenantId } })),
    },

    auth: {
      requestAccess: async (identifier, tenantId, codeChallenge) => {
        await http.post('/portal/login', {
          identifier,
          tenantId,
          ...(codeChallenge ? { codeChallenge } : {}),
        });
      },
      verify: async (token, codeVerifier) =>
        unwrap<VerifyResult>(
          await http.post('/portal/verify', {
            token,
            ...(codeVerifier ? { codeVerifier } : {}),
          })
        ),
    },
  };
}
